import { useSelfHiringContext } from "@/app/contexts/SelfHiringContext";
import { cn } from "@/app/utils/cn";
import { addMoneySimbols } from "@/app/utils/formatMoneyValue";
import { OptionType } from "@/app/utils/optionSelectType";
import { getPartnerBgColor, getPartnerImage } from "@/app/utils/partners";
import { PartnerType } from "@/app/utils/partners.enum";
import { Button } from "@/view/components/ui/button";
import { addMonths, format, isValid, parseISO } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { buildCustomStyles } from "./styles";
import { useTheme } from "@/app/hooks/useTheme";
import { CreditCard } from "@/view/components/CreditCard";
import { useFormContext } from "react-hook-form";
import { useInstallments } from "./useInstallments";
import { CondicaoCredito } from "@/app/services/panService/generateOffers";
import { CreditPartner } from "../../../useCreditController";
import { Skeleton } from "@/view/components/ui/skeleton";
import { useLeadMutations } from "@/app/hooks/useLeadMutations";
import { Input } from "@/view/components/ui/input";
import { Refresh } from "iconsax-react";

interface InstallmentsProps {
  onNext: () => void;
}
export const Installments = ({ onNext }: InstallmentsProps) => {
  const methods = useFormContext<any>();
  const {
    handleChangeInstallment,
    reloadTable,
    handleMakeProposal,
    cltFinancedValue,
    setCltFinancedValue,
    handleCltRefresh,
    cltMessage,
    cltPrazo,
    handleSelectCltPrazo,
  } = useInstallments(methods);
  const { theme } = useTheme();
  const customStyles = useMemo(
    () => buildCustomStyles(theme === "dark"),
    [theme],
  );

  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);
  const { type, fgtsData, panData, cltData, selfHireLinkId } =
    useSelfHiringContext();
  const { updateLead } = useLeadMutations();
  const [openItem, setOpenItem] = useState<{ [index: number]: boolean }>({});
  const isCltInitialLoading = type === PartnerType.CLT && !cltData;
  if (!fgtsData?.table && !panData && !cltData && !isCltInitialLoading)
    return null;

  const panOffer = useMemo(
    () =>
      panData?.state?.offers?.conditions?.find((cond) => cond.sucesso) ?? null,
    [panData?.state?.offers?.conditions],
  );

  const getSeguro = (offer: CondicaoCredito | null) => {
    if (!offer) return null;
    return offer.despesas?.find((d) => d.grupo === "SEGURO");
  };
  const tableFGTS = useMemo(() => fgtsData?.table ?? null, [fgtsData?.table]);

  const tableCLT = useMemo(() => {
    if (!cltData?.tables?.length) return null;
    return cltData.tables[cltData.selectedIndex ?? 0] ?? cltData.tables[0];
  }, [cltData?.tables, cltData?.selectedIndex]);

  const cltInstallments = useMemo(() => {
    if (!tableCLT?.Prazo || !tableCLT?.ValorParcela) return [];
    const baseDateRaw = tableCLT.DataPrimeiroVencimento;
    const baseDate = baseDateRaw ? parseISO(baseDateRaw) : null;
    return Array.from({ length: tableCLT.Prazo }, (_, i) => ({
      number: i + 1,
      value: tableCLT.ValorParcela,
      dueDate:
        baseDate && isValid(baseDate)
          ? addMonths(baseDate, i).toISOString()
          : "",
    }));
  }, [tableCLT]);

  const mapping = {
    [PartnerType.FGTS]: {
      availableValue: tableFGTS?.ValorLiquidoCliente,
      blockedValue: tableFGTS?.ValorBloqueado,
      blockedLabel: "Valor Bloqueado",
      installments: tableFGTS?.Parcelas?.map((parcela) => ({
        number: parcela.NumeroParcela,
        value: parcela.ValorParcela,
        dueDate: parcela.DataVencimento,
      })),
      tc: tableFGTS?.ValorTC ?? 0,
      iof: tableFGTS?.IOF ?? 0,
      seguro: tableFGTS?.ValorSeguro ?? 0,
      prazo: tableFGTS?.Prazo ?? 0,
    },
    [PartnerType.PAN]: {
      availableValue: panOffer?.valor_liquido,
      blockedValue: 0,
      blockedLabel: "Valor Bloqueado",
      installments: panOffer?.parcelas?.map((parcela, i) => ({
        number: i + 1,
        value: panData?.state?.selectedValue ?? 0,
        dueDate: (parcela as string) || "",
      })),
      tc: 0,
      iof: panOffer?.valor_iof ?? 0,
      seguro: getSeguro(panOffer)?.valor_calculado ?? 0,
      prazo: panOffer?.prazo ?? 0,
    },
    [PartnerType.CLT]: {
      availableValue: tableCLT?.ValorLiquidoCliente ?? 0,
      blockedValue: tableCLT?.ValorParcela ?? 0,
      blockedLabel: "Valor da Parcela",
      installments: cltInstallments,
      tc: tableCLT?.ValorTC ?? 0,
      iof: tableCLT?.ValorIOC ?? 0,
      seguro: tableCLT?.ValorSeguro ?? 0,
      prazo: tableCLT?.Prazo ?? 0,
    },
  };

  const table = useMemo(
    () => mapping[type as keyof typeof mapping] ?? null,
    [type, mapping],
  );

  const installmentsLabel = type === PartnerType.CLT ? "Tabela" : "Parcelas";

  const cltPrazoOptions = useMemo(
    () =>
      (cltData?.prazos ?? []).map((prazo) => ({
        label: `${prazo}X`,
        value: `${prazo}`,
      })) as OptionType[],
    [cltData?.prazos],
  );

  const selectedCltPrazoOption = useMemo(() => {
    const prazo = cltPrazo ?? tableCLT?.Prazo;
    if (!prazo) return null;
    return (
      cltPrazoOptions.find((option) => option.value === `${prazo}`) ?? null
    );
  }, [cltPrazoOptions, cltPrazo, tableCLT?.Prazo]);

  const hasCltPrazoSelect =
    type === PartnerType.CLT && cltPrazoOptions.length > 0;

  const items: {
    label: string;
    value?: string;
    subItem?: (props: any) => JSX.Element;
    isCltPrazo?: boolean;
  }[] = [
    {
      label: "Valor Disponível",
      value: addMoneySimbols(table.availableValue ?? 0),
    },
    {
      label: table.blockedLabel ?? "Valor Bloqueado",
      value: addMoneySimbols(table.blockedValue ?? 0),
      subItem: SubValues,
    },
    ...(hasCltPrazoSelect ? [{ label: "Prazo", isCltPrazo: true }] : []),
    {
      label: installmentsLabel,
      subItem: InstallmentItens,
    },
  ];

  const options = useMemo(() => {
    if (type === PartnerType.PAN) {
      const prazoOptions = panData?.state?.prazoOptions ?? [];
      return prazoOptions.map((p) => ({
        label: p.label,
        value: `${p.value}`,
      })) as OptionType[];
    }

    // O CLT é escolhido pela tabela (nome + prazo); o value é o índice da oferta.
    if (type === PartnerType.CLT) {
      const cltTables = cltData?.tables ?? [];
      return cltTables.map((t, index) => ({
        label: t.NomeTabela ? `${t.NomeTabela} - ${t.Prazo}x` : `${t.Prazo}X`,
        value: `${index}`,
      })) as OptionType[];
    }

    return Array.from({ length: table.prazo ?? 0 }, (_, i) => ({
      label: `${i + 1}X`,
      value: `${i + 1}`,
    })) as OptionType[];
  }, [panData?.state?.prazoOptions, cltData?.tables, table.prazo, type]);

  const handleSelectInstallment = (opt: OptionType) => {
    setSelectedOption(opt);

    if (opt.value === "maximo") {
      if (type === PartnerType.CLT) {
        const cltTables = cltData?.tables ?? [];
        if (!cltTables.length) return;

        const maxIndex = cltTables.reduce(
          (best, t, i) => (t.Prazo > cltTables[best].Prazo ? i : best),
          0,
        );
        setSelectedOption(options[maxIndex] ?? null);
        void handleChangeInstallment(maxIndex);
        return;
      }

      if (type === PartnerType.PAN) {
        const maxPrazo = Math.max(
          ...(options
            .map((o) => Number(o.value))
            .filter((n) => Number.isFinite(n)) as number[]),
        );
        if (Number.isFinite(maxPrazo)) void handleChangeInstallment(maxPrazo);
      } else {
        void handleChangeInstallment(undefined);
      }
      return;
    }

    const value = Number(opt.value);
    if (!Number.isFinite(value)) return;

    void handleChangeInstallment(value);
  };

  useEffect(() => {
    if (type === PartnerType.CLT) {
      const index = cltData?.selectedIndex ?? 0;
      setSelectedOption(options[index] ?? null);
      return;
    }

    const prazoAtual =
      type === PartnerType.PAN ? panData?.state?.selectedPrazo : table.prazo;

    if (!prazoAtual) {
      setSelectedOption(null);
      return;
    }

    setSelectedOption(
      options.find((option) => option.value === `${prazoAtual}`) ?? null,
    );
  }, [
    options,
    panData?.state?.selectedPrazo,
    cltData?.selectedIndex,
    table.prazo,
    type,
  ]);

  const isReloading = reloadTable || isCltInitialLoading;

  const handleNext = async () => {
    const form = methods.getValues();

    const value =
      panData?.state?.selectedValue ??
      fgtsData?.table?.ValorLiquidoCliente ??
      tableCLT?.ValorLiquidoCliente;

    try {
      await updateLead({
        link: selfHireLinkId || "",
        cpf: form.cpf?.replace(/\D/g, ""),
        type: type || undefined,
        value: value ?? undefined,
      });
    } catch {}

    if (type === PartnerType.PAN) {
      handleMakeProposal();
    }

    onNext();
  };

  return (
    <>
      <div
        className="mb-4 flex h-14 w-full shrink-0 items-center justify-center rounded-xl"
        style={{ backgroundColor: getPartnerBgColor(type as PartnerType) }}
      >
        <img
          src={getPartnerImage(type as CreditPartner)}
          alt={(type as string) ?? ""}
          className={cn(
            "h-14 object-contain",
            type === PartnerType.PAN && "h-18 max-w-32",
          )}
        />
      </div>
      <CreditCard
        classNameBackButton="md:top-4"
        className="gap-4 pt-12 sm:pt-8 md:pt-12"
      >
        {items?.map((item, i) => {
          const isOpen = openItem[i] ?? false;
          const SubItem = item.subItem;
          const isInstallments = item.label === installmentsLabel;
          const isCltPrazo = !!item.isCltPrazo;
          const isCltAvailableValue = type === PartnerType.CLT && i === 0;

          return (
            <>
              <hr
                className={cn(
                  "h-[2px] w-full bg-gray-400",
                  i == 0 && "h-[3px] bg-primary",
                )}
              />
              <div
                key={item.label}
                className={cn(
                  "flex w-full  flex-col py-1",
                  isInstallments && "py-0",
                )}
              >
                <div className="flex w-full items-center">
                  <span
                    onClick={() =>
                      item.subItem &&
                      setOpenItem((prev) => ({ ...prev, [i]: !isOpen }))
                    }
                    className={cn(
                      "mr-1 text-xs text-primary",
                      item.subItem && "cursor-pointer",
                    )}
                  >
                    {isOpen ? "▼" : "▶"}
                  </span>
                  <span className="aarkrk:texwhithimtd:textbase text-sm leading-none">
                    {item.label}:
                  </span>
                  <span
                    className="mx-1 h-[1px] flex-1"
                    style={{
                      backgroundImage:
                        " repeating-linear-gradient(to right, currentColor 0, currentColor 3px, transparent 3px, transparent 6px)",
                    }}
                  ></span>
                  {isInstallments ? (
                    <div>
                      <Select
                        options={[
                          ...options,
                          { label: "Máximo", value: "maximo" },
                        ]}
                        onChange={(value) =>
                          handleSelectInstallment(value as OptionType)
                        }
                        value={selectedOption}
                        styles={customStyles}
                        isDisabled={isReloading}
                        /*   isLoading={isFetchingCustomer || isLoadingCustomer}
                      onMenuScrollToBottom={handleLoadMoreCustomer} */
                      />
                    </div>
                  ) : isCltPrazo ? (
                    <div>
                      <Select
                        options={cltPrazoOptions}
                        onChange={(option) =>
                          handleSelectCltPrazo(
                            Number((option as OptionType).value),
                          )
                        }
                        value={selectedCltPrazoOption}
                        styles={customStyles}
                        isDisabled={isReloading}
                      />
                    </div>
                  ) : isCltAvailableValue ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={addMoneySimbols(
                          cltFinancedValue ?? table.availableValue ?? 0,
                        )}
                        disabled={isReloading}
                        onChange={(e) => {
                          const onlyNumbers = e.target.value.replace(/\D/g, "");
                          setCltFinancedValue(Number(onlyNumbers) / 100);
                        }}
                        className="h-9 w-32 text-right text-sm font-semibold"
                      />
                      <button
                        type="button"
                        onClick={handleCltRefresh}
                        disabled={isReloading}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-input bg-background disabled:opacity-50"
                      >
                        <Refresh size={16} />
                      </button>
                    </div>
                  ) : isReloading ? (
                    <Skeleton className="h-4 w-24" />
                  ) : (
                    <span className="font-semibold leading-none dark:text-white">
                      {item.value}
                    </span>
                  )}
                </div>

                {SubItem && isOpen && (
                  <SubItem
                    data={table.installments ?? []}
                    tc={table.tc}
                    iof={table.iof}
                    seguro={table.seguro}
                    isReloading={isReloading}
                  />
                )}
              </div>
            </>
          );
        })}

        {type === PartnerType.CLT && cltMessage && (
          <span className="text-center text-sm font-medium text-red-500">
            {cltMessage}
          </span>
        )}

        <Button
          disabled={
            isReloading || (type === PartnerType.CLT && !tableCLT)
          }
          isLoading={isReloading}
          onClick={handleNext}
          className="mt-2 w-full rounded-xl font-semibold text-white"
        >
          Contratar
        </Button>
      </CreditCard>
    </>
  );
};

const SubValues = ({
  tc,
  iof,
  seguro,
  isReloading,
}: {
  tc: number;
  iof: number;
  seguro: number;
  isReloading?: boolean;
}) => {
  const items = [
    {
      label: "Taxa de Cadastro",
      value: addMoneySimbols(tc),
    },
    {
      label: "IOF",
      value: addMoneySimbols(iof),
    },
    {
      label: "Seguro",
      value: addMoneySimbols(seguro),
    },
  ];
  return (
    <div className="mt-6 rounded-xl border border-gray-300 p-2 dark:border-gray-700">
      <table className="w-full border-collapse text-sm">
        <tbody>
          {items.map((item) => (
            <tr key={item.label}>
              <td className="px-3 py-[2px]	text-center text-sm font-medium dark:text-white">
                {item.label}
              </td>
              <td className="border-l border-gray-300	px-3 py-[2px] text-center text-sm font-medium dark:border-gray-700 dark:text-white">
                {isReloading ? (
                  <Skeleton className="mx-auto h-4 w-16" />
                ) : (
                  `-${item.value}`
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const InstallmentItens = ({
  data,
  isReloading,
}: {
  data: { number: number; value: number; dueDate: string }[];
  isReloading?: boolean;
}) => {
  const styleValues =
    "px-4 py-[2px] text-sm font-medium text-left dark:text-white";

  const safeFormatDate = (dueDate: string) => {
    const parsed = parseISO(dueDate);
    if (isValid(parsed)) return format(parsed, "dd/MM/yyyy");

    const fallback = new Date(dueDate);
    if (isValid(fallback)) return format(fallback, "dd/MM/yyyy");

    return dueDate;
  };

  const rows =
    isReloading && (!data || data.length === 0)
      ? Array.from({ length: 6 }, (_, i) => ({
          number: i + 1,
          value: 0,
          dueDate: "",
        }))
      : data;

  return (
    <div className="mt-6 rounded-xl border border-gray-300 p-2 dark:border-gray-700">
      <table className="w-full border-collapse text-sm">
        <tbody>
          {rows?.map((item: any) => (
            <tr key={item.number}>
              <td className={styleValues}>{item.number}ª Parcela</td>

              <td
                className={cn(
                  styleValues,
                  "border-l border-gray-300 text-center",
                )}
              >
                {isReloading ? (
                  <Skeleton className="mx-auto h-4 w-20" />
                ) : (
                  addMoneySimbols(item.value)
                )}
              </td>

              <td
                className={cn(
                  styleValues,
                  "border-l border-gray-300 text-right dark:border-gray-700",
                )}
              >
                {isReloading ? (
                  <Skeleton className="ml-auto h-4 w-20" />
                ) : (
                  safeFormatDate(item.dueDate)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
